/**
 * commitWorkshopStep — step-1 persistence for the onboarding wizard (#1853).
 *
 * The workshop name is a `tenants` column and everything else is in the
 * settings JSONB, so this step is two requests, not one
 * (`WORKSHOP_SETTINGS_PAYLOAD_KEYS.name === null` records why).
 *
 * Order and idempotency follow `commitCustomerVehicleStep`: the name goes
 * first, `onNameSaved` fires the moment it lands, and the caller uses that to
 * update wizard state. If the settings request then fails, the retry starts
 * from the already-updated state and skips the name — nothing is written twice.
 */

import { api } from '@/lib/api';
import { WORKSHOP_SETTINGS_PAYLOAD_KEYS } from '@/features/settings/workshopSettingsPayload';
import type { WorkshopSettings } from '@/types';

/** The fields step 1 shows. Anything else must not appear in the PATCH. */
const EDITED_FIELDS = [
  'address',
  'city',
  'postalCode',
  'businessId',
  // One of these is required before a payment can be recorded; the wizard
  // asks for both since #2495.
  'phone',
  'email',
  'bankIban',
] as const satisfies readonly (keyof WorkshopSettings)[];

const emptyToUndefined = (v: string | undefined) => (v === '' ? undefined : v);

function pickEdited(settings: WorkshopSettings): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const field of EDITED_FIELDS) {
    // The key map is `Record<keyof WorkshopSettings, …>`, so a renamed field
    // fails typecheck here rather than silently going missing (#1795).
    const settingsKey = WORKSHOP_SETTINGS_PAYLOAD_KEYS[field];
    if (settingsKey) payload[settingsKey] = emptyToUndefined(settings[field] as string | undefined);
  }

  // Staff enter the VAT-inclusive rate; the API derives and stores the net one
  // and never persists a second copy (#1600/#1708). A cleared field is a real
  // zero, and a zero gross is ignored server-side, so the net has to carry it.
  payload.laborRateCents =
    settings.laborRateGrossCents === 0 ? 0 : settings.laborRateCents;
  payload.laborRateGrossCents = settings.laborRateGrossCents;

  return payload;
}

export interface CommitWorkshopStepInput {
  settings: WorkshopSettings;
  /** True once the name has been persisted, so a retry does not resend it. */
  nameAlreadySaved: boolean;
  onNameSaved: () => void;
}

interface TenantSettingsResponse {
  settings: {
    laborRateCents?: number;
    laborRateGrossCents?: number;
  };
}

export interface CommitWorkshopStepResult {
  /** Net rate as the API recomputed it — never the pre-save local value. */
  laborRateCents: number | undefined;
  laborRateGrossCents: number | undefined;
}

export async function commitWorkshopStep({
  settings,
  nameAlreadySaved,
  onNameSaved,
}: CommitWorkshopStepInput): Promise<CommitWorkshopStepResult> {
  if (!nameAlreadySaved && settings.name) {
    await api.patch('/tenants/me', { name: settings.name });
    onNameSaved();
  }

  // Only the fields this step actually edits.
  //
  // NOT `buildWorkshopSettingsPayload`, even though that is the guarded helper
  // for the settings tab: it emits every key, and the route merges with a
  // shallow `...body`. Sending `vatRates` or `businessHours` from a wizard that
  // never showed them would overwrite what provisioning seeded — a blanked VAT
  // table on a workshop that had one. A partial PATCH leaves untouched keys
  // untouched.
  const payload = pickEdited(settings);

  const updated = await api.patch<TenantSettingsResponse>('/tenants/me/settings', payload);

  return {
    laborRateCents: updated.settings.laborRateCents,
    laborRateGrossCents: updated.settings.laborRateGrossCents,
  };
}
